import React from 'react';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Container from 'react-bootstrap/Container';
import { formatUSD } from '../../utils/format';
import './index.scss';
import { useGetBlacklisted, useNetworkHumanReadableName } from '../../hooks/useNetwork';
import useOffchainInstance, { useOffchainAccount } from '../../hooks/useOffchain';
import useGenericCall from '../../hooks/useGenericCall';
import { Factory, FactoryIndex, OffchainAccount, PoolTokenType, offchain } from 'impermax-sdk';
import { getBorrowPositions, getSupplyPositions } from '../AccountPage';


export default function OverallStats() {
  const data = useGenericCall(useOffchainInstance(), async offchainInstance => ({
    crossChainTVL: await offchainInstance.getCrossChainTVL(),
    totalValueLocked: await offchainInstance.getTotalValueLocked(),
    totalValueSupplied: await offchainInstance.getTotalValueSupplied(),
    totalValueBorrowed: await offchainInstance.getTotalValueBorrowed(),
  }));
  const networkHRName = useNetworkHumanReadableName();

  if (!data) return (null);

  return(<Container className="overall-stats">
    <Card>
      <Row>
        <Col xs={12} md={3} className="col">
          <div className="name">Cross-chain TVL:</div>
          <div className="value">{ formatUSD(data.crossChainTVL) }</div>
        </Col>
        <Col xs={12} md={3} className="col">
          <div className="name">{ networkHRName } TVL:</div>
          <div className="value">{ formatUSD(data.totalValueLocked) }</div>
        </Col>
        <Col xs={12} md={3} className="col">
          <div className="name">Total supplied:</div>
          <div className="value">{ formatUSD(data.totalValueSupplied) }</div>
        </Col>
        <Col xs={12} md={3} className="col">
          <div className="name">Total borrowed:</div>
          <div className="value">{ formatUSD(data.totalValueBorrowed) }</div>
        </Col>
      </Row>
    </Card>
  </Container>);
}

async function getAccountTotalValueLocked(offchainAccount: OffchainAccount, borrowPositions: FactoryIndex<string[]>, supplyPositions: FactoryIndex<string[]>) : Promise<number> {
  let borrowPositionsTVL = 0;
  for (const factory of Object.keys(borrowPositions) as Factory[]) {
    for (const uniswapV2PairAddress of borrowPositions[factory]) {
      const offchainAccountLendingPool = await offchainAccount.getLendingPool(factory, uniswapV2PairAddress);
      borrowPositionsTVL += await offchainAccountLendingPool.getBorrowerEquityValue();
    }
  }
  return borrowPositionsTVL + await getAccountTotalValueSupplied(offchainAccount, supplyPositions);
}

async function getAccountTotalValueSupplied(offchainAccount: OffchainAccount, supplyPositions: FactoryIndex<string[]>) : Promise<number> {
  let tvl = 0;
  for (const factory of Object.keys(supplyPositions) as Factory[]) {
    for (const uniswapV2PairAddress of supplyPositions[factory]) {
      const offchainAccountLendingPool = await offchainAccount.getLendingPool(factory, uniswapV2PairAddress);
      tvl += await offchainAccountLendingPool.getBorrowableA().getValue();
      tvl += await offchainAccountLendingPool.getBorrowableB().getValue();
    }
  }
  return tvl;
}

async function getAccountTotalValueBorrowed(offchainAccount: OffchainAccount, borrowPositions: FactoryIndex<string[]>) : Promise<number> {
  let tvl = 0;
  for (const factory of Object.keys(borrowPositions) as Factory[]) {
    for (const uniswapV2PairAddress of borrowPositions[factory]) {
      const offchainAccountLendingPool = await offchainAccount.getLendingPool(factory, uniswapV2PairAddress);
      tvl += await offchainAccountLendingPool.getBorrowableA().getBorrowedValue();
      tvl += await offchainAccountLendingPool.getBorrowableB().getBorrowedValue();
    }
  }
  return tvl;
}

export function AccountOverallStats() {
  const getBlacklisted = useGetBlacklisted();
  const offchainAccount = useOffchainAccount();
  const data = useGenericCall(offchainAccount, async s => {
    const borrowPositions = await getBorrowPositions(offchainAccount, getBlacklisted);
    const supplyPositions = await getSupplyPositions(offchainAccount, getBlacklisted);
    return {
      totalValueLocked: await getAccountTotalValueLocked(offchainAccount, borrowPositions, supplyPositions),
      totalValueSupplied: await getAccountTotalValueSupplied(offchainAccount, supplyPositions),
      totalValueBorrowed: await getAccountTotalValueBorrowed(offchainAccount, borrowPositions),
    }
  });
  const networkHRName = useNetworkHumanReadableName();

  if (!data) return (null);

  return(<Container className="overall-stats">
    <Card>
      <Row>
        <Col xs={12} md={4} className="col">
          <div className="name">{ networkHRName } TVL:</div>
          <div className="value">{ formatUSD(data.totalValueLocked) }</div>
        </Col>
        <Col xs={12} md={4} className="col">
          <div className="name">Total supplied:</div>
          <div className="value">{ formatUSD(data.totalValueSupplied) }</div>
        </Col>
        <Col xs={12} md={4} className="col">
          <div className="name">Total borrowed:</div>
          <div className="value">{ formatUSD(data.totalValueBorrowed) }</div>
        </Col>
      </Row>
    </Card>
  </Container>)
}